'use client';

import { useQuery } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import { useTranslations } from 'next-intl';
import { ComponentPropsWithRef } from 'react';
import { useCartStore, getPriceForItem, getPriceValueTranslation } from '@/entities/cart';
import { QueryApi } from '@/features/query/api';
import { cn } from '@/shared/lib/cn';
import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';
import { Country } from '@/shared/model/Country';
import { Currency } from '@/shared/model/Currency';
import { Icon } from '@/shared/ui/icon';

type Props = {
    start: Dayjs;
    end: Dayjs;
    currency: Currency;
    isActive?: boolean;
    hasEcoDiscount?: boolean;
} & ComponentPropsWithRef<'button'>;
export const CartTimedOrdersBoxTimeSlot = ({
    start,
    end,
    currency,
    isActive,
    hasEcoDiscount = false,
    className,
    ...props
}: Props) => {
    const t = useTranslations();
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const [selectedCurrency] = useSelectedCurrency();
    const venueCountry = (venueQuery.data?.address?.country as Country) ?? Country.CZ;
    const selectedDeliveryMethod = useCartStore((s) => s.deliveryMethod);
    const deliveryMethodPrice = getPriceForItem(
        { prices: [selectedDeliveryMethod?.taxedPrice] },
        venueCountry,
        selectedCurrency,
    );
    const areaPrice = useCartStore((s) => s.areaPrice);

    const time = `${start.format('HH:mm')} - ${end.format('HH:mm')}`;

    const deliveryMethodWithAreaPrice =
        parseFloat(deliveryMethodPrice?.amount || '0') + parseFloat(areaPrice?.amount ?? '0');
    const ecoDiscountPercentage = selectedDeliveryMethod?.extensionTimedOrders?.ecoDiscount.percentage ?? 0;
    const amount = Math.max(
        hasEcoDiscount
            ? deliveryMethodWithAreaPrice * (1 - ecoDiscountPercentage / 100)
            : deliveryMethodWithAreaPrice,
        0,
    );

    return (
        <button
            type="button"
            className={cn(
                'cart-timed-orders-box-time-slot',
                isActive && 'cart-timed-orders-box-time-slot--active',
                className,
            )}
            data-testid="cart-timed-orders-time-slot"
            {...props}
        >
            <div className="cart-timed-orders-box-time-slot__time-wrapper">
                {hasEcoDiscount && (
                    <Icon
                        name="leaf"
                        width={16}
                        height={16}
                        className="cart-timed-orders-box-time-slot__icon"
                    />
                )}
                <span data-testid="cart-timed-orders-time-slot-time">{time}</span>
            </div>
            <span
                className={cn(
                    'cart-timed-orders-box-time-slot__price',
                    isActive && 'cart-timed-orders-box-time-slot__price--active',
                )}
                data-testid="cart-timed-orders-time-slot-price"
            >
                {getPriceValueTranslation({ t, amount, currency })}
            </span>
        </button>
    );
};
